import type { PoliceResult } from '@creanizer/types'
import { Shield, AlertTriangle, MapPin, Clock, Users, FileText, Camera, Fingerprint } from 'lucide-react'

interface PoliceViewerProps {
  document: PoliceResult
}

export function PoliceViewer({ document }: PoliceViewerProps) {
  // データ検証
  if (!document || !document.header || !document.incident) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">警察調書</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // ステータスのスタイル取得
  const getStatusStyle = (status?: string) => {
    if (!status) return 'bg-gray-100 text-gray-700'
    const s = status.toLowerCase()
    if (s.includes('解決') || s.includes('closed')) return 'bg-green-100 text-green-700'
    if (s.includes('捜査中') || s.includes('active')) return 'bg-yellow-100 text-yellow-700'
    if (s.includes('保留') || s.includes('pending')) return 'bg-orange-100 text-orange-700'
    return 'bg-gray-100 text-gray-700'
  }
  
  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
      {/* ヘッダー（警察署情報） */}
      <div className="bg-gradient-to-r from-gray-900 to-blue-900 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">{document.header.department}</h1>
              <p className="text-blue-200 text-sm">{document.reportType}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-blue-200">事件番号</p>
            <p className="font-mono font-bold text-lg">{document.header.caseNumber}</p>
            {document.metadata?.status && (
              <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${getStatusStyle(document.metadata.status)}`}>
                {document.metadata.status}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* 警告バー */}
      <div className="bg-yellow-500 text-yellow-900 px-4 py-2 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        <span className="text-sm font-medium">機密情報 - 取扱注意</span>
      </div>
      
      {/* 調書本体 */}
      <div className="p-6">
        {/* 作成情報 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded">
          <div>
            <p className="text-xs text-gray-600 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              作成日時
            </p>
            <p className="font-medium">{document.header.date} {document.header.time}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              場所
            </p>
            <p className="font-medium">{document.header.location}</p>
          </div>
        </div>
        
        {/* 事件概要 */}
        <div className="mb-6 p-4 border-2 border-red-200 bg-red-50 rounded">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            事件概要
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">事件種別</p>
              <p className="font-medium text-red-700">{document.incident.type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">発生日時</p>
              <p className="font-medium">{document.incident.date} {document.incident.time}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600">発生場所</p>
              <p className="font-medium">{document.incident.location}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600 mb-2">概要</p>
              <p className="text-gray-700 bg-white p-3 rounded">{document.incident.summary}</p>
            </div>
          </div>
        </div>
        
        {/* 関係者情報 */}
        {document.involved && document.involved.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              関係者情報
            </h3>
            <div className="space-y-4">
              {document.involved.map((person, index) => (
                <div key={index} className="border border-gray-200 rounded p-4">
                  <div className="flex items-start justify-between mb-3">
                    <span className={`px-2 py-1 text-xs rounded font-medium ${
                      person.role === '被疑者' ? 'bg-red-100 text-red-700' :
                      person.role === '被害者' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {person.role}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-600">氏名</p>
                      <p className="font-medium">{person.name}</p>
                    </div>
                    {person.age && (
                      <div>
                        <p className="text-xs text-gray-600">年齢</p>
                        <p className="font-medium">{person.age}</p>
                      </div>
                    )}
                    {person.address && (
                      <div>
                        <p className="text-xs text-gray-600">住所</p>
                        <p className="font-medium text-sm">{person.address}</p>
                      </div>
                    )}
                  </div>
                  {person.statement && (
                    <div>
                      <p className="text-xs text-gray-600 mb-1">供述内容</p>
                      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                        「{person.statement}」
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* 証拠 */}
        {document.evidence && document.evidence.length > 0 && (
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Camera className="w-5 h-5 text-green-600" />
              証拠物件
            </h3>
            <div className="bg-gray-50 rounded p-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 text-sm">番号</th>
                    <th className="text-left py-2 text-sm">種別</th>
                    <th className="text-left py-2 text-sm">説明</th>
                    <th className="text-left py-2 text-sm">発見場所</th>
                  </tr>
                </thead>
                <tbody>
                  {document.evidence.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-2 text-sm">{index + 1}</td>
                      <td className="py-2 text-sm">{item.type}</td>
                      <td className="py-2 text-sm">{item.description}</td>
                      <td className="py-2 text-sm">{item.location || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* 詳細記述 */}
        <div className="mb-6">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            詳細記述
          </h3>
          <div className="bg-gray-50 p-4 rounded">
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {document.narrative}
            </p>
          </div>
        </div>
        
        {/* 担当官署名 */}
        <div className="mt-8 pt-4 border-t-2 border-gray-300">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm text-gray-600">担当官</p>
              <p className="font-medium text-lg">{document.officer.rank} {document.officer.name}</p>
              <p className="text-sm text-gray-500">警察官番号: {document.officer.badge}</p>
            </div>
            {document.officer.signature && (
              <div>
                <p className="text-sm text-gray-600 mb-1">署名</p>
                <div className="border-b-2 border-gray-400 pb-1">
                  <p className="font-mono text-xl italic">{document.officer.signature}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* フッター */}
      <div className="bg-gray-100 px-6 py-3 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>文字数: {document.metadata.wordCount}</span>
          <span>生成日時: {document.metadata.generatedAt}</span>
        </div>
      </div>
    </div>
  )
}